import React, {useState, useEffect, useRef} from 'react';
import {
  Flex,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Td,
  Table,
  TableCaption,
  Tfoot,
  IconButton,
  useDisclosure,
  Divider,
  Box,
  Spacer,
} from '@chakra-ui/react';
import LoadingWheel from '../../components/General/LoadingWheel';
import {get, postDelete, queryStringBuilder} from '../../service/ApiService';
import {Batch, SMaster} from '../../service/Urls';
import {FaTrashAlt, FaEdit, FaSearch} from 'react-icons/fa';
import {useNavigate} from 'react-router-dom';
import ModalYesNo from '../../components/General/ModalYesNo';
import {setGrid, setSearchHeaders, setFilterHeaders} from '../../helpers/SetGrid';
import ErrorHelper from '../../service/ErrorHelper';
import BoxCard from '../../components/General/BoxCard';
import {SubmitButton} from '../../components/General/FormInputs';
import {useForm} from 'react-hook-form';
import ServiceTypeList from '../../components/General/ServiceTypeList';
import {ImportReport} from '../Surveys/ImportReport';

const BatchSearch = () => {
  const [masterData, setMasterData] = useState([]);
  const [toastResponse, setToastResponse] = useState(undefined);
  const [searchHeaders, setHeaders] = useState({});

  const [forDelete, setForDelete] = useState({id: '', masterName: ''});
  const [elements, setElements] = useState({body: {}, header: {}, footer: {}});
  const [isLoading, setLoading] = useState(true);

  const [pages, setPages] = useState({
    pageNum: 1,
    totalPages: 5,
    itemsPerPage: 5,
    getNewPage: curPage => {
      loadData(curPage);
    },
  });

  const navigate = useNavigate();
  const {isOpen, onOpen, onClose} = useDisclosure();
  const {
    register,
    handleSubmit,
    control,
    formState: {errors},
  } = useForm();
  // useRef will save this across renders so it's not reloaded every time
  // to add more fields to the table, simply add their name and type to this json
  // if a new type is added you'll have to update the table to reflect that type (ie. dropdown or button)
  const visibleData = useRef({
    batchNumber: 'text',
    clientName: 'text',
    importDate: 'text',
    startDate: 'text',
    endDate: 'text',
  });

  // What the id's and types of the formdata should be that is sent to the api when posting
  const searchIds = useRef({
    BatchNumber: 'number',
    ClientName: 'text',
    ImportDate: 'text',
    FromDate: 'text',
    ToDate: 'text',
  });

  // Initial load of the page
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    // initial search values
    const queryParams = {
      Page: 1,
      PageSize: 20,
      ServiceType: 1,
      OrderBy: 'batchNumber',
      IsDescendingSort: true,
    };
    queryData(queryParams);
  };

  // Need this to repopulate everything on the UI any time the data from the web changes
  useEffect(() => {
    masterData.map(record => {
      if (record.importDate) {
        record.importDate = record.importDate.replaceAll('-', '/');
        record.importDate = record.importDate.substring(0, record.importDate.indexOf('T'));
      }
      if (record.startDate) {
        record.startDate = record.startDate.replaceAll('-', '/');
        record.startDate = record.startDate.substring(0, record.startDate.indexOf('T'));
      }
      if (record.endDate) {
        record.endDate = record.endDate.replaceAll('-', '/');
        record.endDate = record.endDate.substring(0, record.endDate.indexOf('T'));
      }
    });
    populateData();
  }, [masterData]);

  // if no masterId is passed in, we'll assume we're adding.
  const edit = masterId => {
    navigate('edit', {state: {id: masterId}});
  };

  const setForDeletion = id => {
    const record = masterData.filter(record => record.id == id);
    setForDelete({id: id, recordNumber: record[0].recordNumber});
    onOpen();
  };

  const deleteMaster = async () => {
    const data = await postDelete(SMaster.DELETE, `"${forDelete.id}"`);
    setToastResponse({response: data, successMessage: 'SuccessFully deleted record ' + forDelete.recordNumber});
    if (!data.error) {
      loadData();
      onClose();
    }
  };
  useEffect(() => {
    setLoading(true);
    loadData(pages.pageNum);
  }, [pages]);

  const populateData = () => {
    // for the function parameters passed in we're going to use the id of data being populated, can do other stuff with it but that was best solution I could come up with
    // this id will be set in the setGrid when the data is populated... really hoping all of our data will have id's!
    const ExtraColumns = ({id}) => {
      return (
        <>
          <Td>
            <IconButton colorScheme="tertiary" title="Edit" size="md" icon={<FaEdit />} mr="2" onClick={() => edit(id)} />
            <IconButton colorScheme="tertiary" title="Delete" size="md" icon={<FaTrashAlt />} onClick={() => setForDeletion(id)} />
          </Td>
        </>
      );
    };
    setGrid(visibleData, masterData, pages, setElements, ExtraColumns);
    setSearchHeaders(searchIds, setHeaders, register, errors, ascFunc, descFunc);
  };

  const search = async formData => {
    // delete any empty parameters
    formData.OrderBy = 'dateImportReport';
    formData.IsDescendingSort = true;
    Object.keys(formData).forEach(k => !formData[k] && formData[k] !== undefined && delete formData[k]);
    await queryData(formData);
  };

  const queryData = async formData => {
    formData.Page = 1;
    formData.PageSize = 10;
    const data = await get(queryStringBuilder(Batch.LIST, formData));

    setToastResponse({response: data});
    if (!data.error) {
      setMasterData(data.data);
      setLoading(false);
    }
  };

  const ascFunc = async orderBy => {
    setLoading(true);
    handleSubmit(data => {
      Object.keys(data).forEach(k => !data[k] && data[k] !== undefined && delete data[k]);
      data.OrderBy = orderBy;
      data.IsDescendingSort = false;
      queryData(data);
    });
  };

  const descFunc = async orderBy => {
    setLoading(true);
    handleSubmit(data => {
      Object.keys(data).forEach(k => !data[k] && data[k] !== undefined && delete data[k]);
      data.OrderBy = orderBy;
      data.IsDescendingSort = true;
      queryData(data);
    });
  };

  return (
    <>
      {isLoading ? (
        <>
          <LoadingWheel />
          <ErrorHelper toastResponse={toastResponse} />
        </>
      ) : (
        <form onSubmit={handleSubmit(search)}>
          <BoxCard width="80%">
            <Flex width="80%">
              {/* <CustomInput register={register} name="Batch Number" id="BatchNumber" errors={errors} requireInt />
              <CustomInput register={register} name="Record Number" id="RecordNumber" errors={errors} requireInt /> */}
              <Box mt="5">
                <ServiceTypeList control={control} />
              </Box>
              <Spacer />
              <SubmitButton name="Search" rightIcon={<FaSearch />} mb="2" mr="5" colorScheme="tertiary" w="25%" alignSelf="end" />
            </Flex>

            <Divider mt="5" colorScheme={'mint'} width={{base: '100%', '2xl': '85%'}} />
            <TableContainer width={{base: '100%', '2xl': '85%'}}>
              <Table variant="striped" colorScheme="mint" size={'md'}>
                <TableCaption>Batch Search</TableCaption>
                <Thead>
                  <Tr>
                    {searchHeaders}
                    <Td px="-2"></Td>
                  </Tr>
                </Thead>
                <Tbody>{elements.body}</Tbody>
                <Tfoot>{/* <Tr>{elements.footer}</Tr> */}</Tfoot>
              </Table>
            </TableContainer>
            <ModalYesNo
              isOpen={isOpen}
              onOpen={onOpen}
              onClose={onClose}
              title="Delete record"
              body={'Are you sure you want to delete record ' + forDelete.recordNumber}
              yesFunction={deleteMaster}
            />
          </BoxCard>
          <ErrorHelper toastResponse={toastResponse} />
        </form>
      )}
    </>
  );
};

export default BatchSearch;
